import React, { useState, useCallback, useEffect, useRef } from 'react';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { PARTNER_SLIDES } from '../data/partners';
import { mediaUrl } from '../utils/mediaUrl';
import AnimatedSection from './AnimatedSection';
import './PartnersSection.css';

const SLIDE_MS = 2500;
const SWIPE_MIN = 40;

const PartnersSection = () => {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const reduceMotion = useReducedMotion();
  const touchStart = useRef(null);
  const len = PARTNER_SLIDES.length;
  const slide = PARTNER_SLIDES[index];

  const go = useCallback(
    (delta) => {
      setIndex((prev) => (prev + delta + len) % len);
    },
    [len]
  );

  useEffect(() => {
    if (paused || reduceMotion) return undefined;
    const t = setInterval(() => {
      setIndex((prev) => (prev + 1) % len);
    }, SLIDE_MS);
    return () => clearInterval(t);
  }, [paused, reduceMotion, len]);

  useEffect(() => {
    const onKey = (e) => {
      const section = document.getElementById('partners');
      if (!section) return;
      const rect = section.getBoundingClientRect();
      const inView = rect.top < window.innerHeight && rect.bottom > 0;
      if (!inView) return;
      if (e.key === 'ArrowLeft') go(-1);
      if (e.key === 'ArrowRight') go(1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [go]);

  const onTouchStart = (e) => {
    const t = e.changedTouches[0];
    touchStart.current = { x: t.clientX, y: t.clientY };
    setPaused(true);
  };

  const onTouchEnd = (e) => {
    const start = touchStart.current;
    touchStart.current = null;
    setPaused(false);
    if (!start) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - start.x;
    const dy = t.clientY - start.y;
    if (Math.abs(dx) < SWIPE_MIN || Math.abs(dx) < Math.abs(dy)) return;
    go(dx < 0 ? 1 : -1);
  };

  return (
    <section className="partners section" id="partners">
      <div className="container">
        <AnimatedSection>
          <p className="section-label">/ Partners & clients</p>
          <h2 className="section-title">Trusted where AI has to hold.</h2>
          <p className="section-subtitle">
            Official tech partners Byondbites, Valken, and SelfAgentic — plus our own
            product and AI shipped for clients across safety, parks, infrastructure,
            cameras, and energy.
          </p>
        </AnimatedSection>

        <div
          className="partners-carousel"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          onFocusCapture={() => setPaused(true)}
          onBlurCapture={(e) => {
            if (!e.currentTarget.contains(e.relatedTarget)) setPaused(false);
          }}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
        >
          <button
            type="button"
            className="partners-side-btn partners-side-btn--prev"
            aria-label="Previous partner"
            onClick={() => go(-1)}
          >
            <FaChevronLeft />
          </button>

          <div className="partners-stage" aria-live="polite">
            <AnimatePresence mode="wait">
              <motion.article
                key={slide.id}
                className={`partner-slide partner-slide--${slide.kind}`}
                initial={reduceMotion ? false : { opacity: 0, x: 28 }}
                animate={{ opacity: 1, x: 0 }}
                exit={reduceMotion ? undefined : { opacity: 0, x: -28 }}
                transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              >
                <div className="partner-slide-media">
                  {slide.video ? (
                    <video
                      key={slide.video}
                      src={mediaUrl(slide.video)}
                      poster={mediaUrl(slide.image)}
                      muted
                      playsInline
                      loop
                      autoPlay
                      preload="metadata"
                    />
                  ) : (
                    <img
                      src={mediaUrl(slide.image)}
                      alt={slide.alt || slide.name}
                      loading="lazy"
                    />
                  )}
                </div>

                <div className="partner-slide-copy">
                  <p className="partner-badge">{slide.badge}</p>
                  <h3>
                    <a href={slide.href} target="_blank" rel="noopener noreferrer">
                      {slide.name}
                    </a>
                  </h3>
                  <p className="partner-role">{slide.role}</p>
                  {slide.tagline && (
                    <p className="partner-tagline">{slide.tagline}</p>
                  )}
                  <p className="partner-summary">{slide.summary}</p>
                  {slide.meta?.length > 0 && (
                    <ul className="partner-focus">
                      {slide.meta.map((item) => (
                        <li key={item}>{item}</li>
                      ))}
                    </ul>
                  )}
                  <a
                    href={slide.href}
                    className="partner-link"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Open {slide.name} →
                  </a>
                  <p className="partner-count">
                    {index + 1} / {len}
                  </p>
                </div>
              </motion.article>
            </AnimatePresence>
          </div>

          <button
            type="button"
            className="partners-side-btn partners-side-btn--next"
            aria-label="Next partner"
            onClick={() => go(1)}
          >
            <FaChevronRight />
          </button>

          <div className="partners-controls" aria-label="Partner navigation">
            <button
              type="button"
              className="partners-side-btn partners-side-btn--prev-mobile"
              aria-label="Previous partner"
              onClick={() => go(-1)}
            >
              <FaChevronLeft />
            </button>
            <div className="partners-dots" role="tablist" aria-label="Partners">
              {PARTNER_SLIDES.map((s, i) => (
                <button
                  key={s.id}
                  type="button"
                  role="tab"
                  aria-selected={i === index}
                  className={`partners-dot ${i === index ? 'active' : ''}`}
                  aria-label={s.name}
                  onClick={() => setIndex(i)}
                />
              ))}
            </div>
            <button
              type="button"
              className="partners-side-btn partners-side-btn--next-mobile"
              aria-label="Next partner"
              onClick={() => go(1)}
            >
              <FaChevronRight />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PartnersSection;
